export type CoverCrop = {
  /** Left edge of the source rect to copy, in source pixels. */
  x: number;
  /** Top edge of the source rect to copy, in source pixels. */
  y: number;
  width: number;
  height: number;
};

type Args = {
  sourceWidth: number;
  sourceHeight: number;
  targetWidth: number;
  targetHeight: number;
};

/**
 * The largest centred rect of the source that matches the target's aspect ratio — CSS `object-fit:
 * cover`, expressed as coordinates `drawImage` can take.
 *
 * Cover rather than contain: Agora composites the background with `fit: 'cover'` and the picker
 * thumbnail is `objectFit: 'cover'` too, so cropping at import is what makes the 72px tile an exact
 * preview of what participants will actually see. Containing would letterbox a portrait photo with
 * bars that then get baked into the stored image.
 *
 * Pure on purpose: it's the only part of the import pipeline that can be arithmetically wrong, and
 * happy-dom has no canvas, so this is where the real tests live.
 */
export const calculateCoverCrop = ({ sourceWidth, sourceHeight, targetWidth, targetHeight }: Args): CoverCrop => {
  const sourceAspect = sourceWidth / sourceHeight;
  const targetAspect = targetWidth / targetHeight;

  // Source is proportionally wider than the target — use its full height and crop the sides.
  if (sourceAspect > targetAspect) {
    const width = sourceHeight * targetAspect;

    return { x: (sourceWidth - width) / 2, y: 0, width, height: sourceHeight };
  }

  // Source is proportionally taller (or an exact match) — use its full width and crop top/bottom.
  const height = sourceWidth / targetAspect;

  return { x: 0, y: (sourceHeight - height) / 2, width: sourceWidth, height };
};
